/**
 * @fileoverview Unlock prompt for a previously-saved, password-encrypted
 * Winbit32 seed. Shown on return visits when `getStoredMnemonicInfo()` reports
 * an encrypted record, so the user types their password rather than re-pasting
 * the whole mnemonic.
 */

import React, { useState } from 'react';
import { X, LockKeyhole } from 'lucide-react';

interface WalletUnlockModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Decrypt + connect with the supplied password. Throws on wrong password. */
	onUnlock: (password: string) => Promise<void>;
	/** Switch to the "enter a different seed" flow. */
	onUseDifferentSeed: () => void;
	/** Wipe the saved (encrypted) seed from this device. */
	onForget: () => void;
}

const WalletUnlockModal: React.FC<WalletUnlockModalProps> = ({
	isOpen,
	onClose,
	onUnlock,
	onUseDifferentSeed,
	onForget,
}) => {
	const [password, setPassword] = useState('');
	const [isUnlocking, setIsUnlocking] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const reset = () => {
		setPassword('');
		setIsUnlocking(false);
		setError(null);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleUnlock = async () => {
		if (!password) {
			setError('Enter your password.');
			return;
		}
		setError(null);
		setIsUnlocking(true);
		try {
			await onUnlock(password);
			reset();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Could not unlock your wallet.';
			setError(message);
		} finally {
			setIsUnlocking(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

			<div className="flex items-center justify-center min-h-screen p-4">
				<div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full">
					<div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-xl">
						<div className="flex items-center justify-between">
							<h2 className="text-xl font-bold text-white">Unlock your wallet</h2>
							<button
								onClick={handleClose}
								className="text-white hover:text-gray-200 transition-colors"
								aria-label="Close modal"
							>
								<X className="w-6 h-6" />
							</button>
						</div>
						<p className="text-purple-100 text-sm mt-2">
							Your seed is stored encrypted on this device. Enter the password you set.
						</p>
					</div>

					<form
						className="p-6"
						noValidate
						onSubmit={(e) => {
							e.preventDefault()
							if (!isUnlocking && password) void handleUnlock()
						}}
					>
						{error && (
							<div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
								<p className="text-sm text-red-800">{error}</p>
							</div>
						)}

						<label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
						<input
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoFocus
							autoComplete="current-password"
							placeholder="Your wallet password"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
						/>

						<button
							type="button"
							onClick={handleUnlock}
							disabled={isUnlocking || !password}
							className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
						>
							{isUnlocking ? (
								<>
									<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
									Unlocking...
								</>
							) : (
								<>
									<LockKeyhole className="w-4 h-4 mr-2" />
									Unlock
								</>
							)}
						</button>

						<div className="mt-4 flex items-center justify-between text-xs">
							<button
								type="button"
								onClick={() => {
									reset();
									onUseDifferentSeed();
								}}
								className="text-purple-700 hover:text-purple-900 underline underline-offset-2"
							>
								Enter a different seed
							</button>
							<button
								type="button"
								onClick={() => {
									reset();
									onForget();
								}}
								className="text-red-600 hover:text-red-800 underline underline-offset-2"
							>
								Forget saved wallet
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default WalletUnlockModal;
